import React, { useState, useEffect } from 'react';
import { AgGridReact } from 'ag-grid-react';
import 'ag-grid-community/styles/ag-grid.css';
import 'ag-grid-community/styles/ag-theme-alpine.css';
import { fetchLatest990FromSupabase, fetchAwardsReceivedFromSupabase, fetchAwardsGrantedFromSupabase } from '../../utilityFunctions/supabaseUtilities';
import FunderRenderer from '../HelperComponents/FunderRenderer';
import RecipientRenderer from '../HelperComponents/RecipientRenderer';
import './OrganizationDetailsBody.css';
import MagicFirstDraftModal from '../WritingComponents/MagicFirstDraftModal';

function OrganizationProfilePage({ 
    currentProgram,
    referrerOrganizationEin,
    setShowLoadingModal,
    membershipDetails,
    setShowPaywallModal,
    setShowMagicFirstDraftModal,
    setFunderNameForMagicDraft,
    setFunderDescriptionForMagicDraft
}) {    
    const ein = referrerOrganizationEin;
    const [organization, setOrganization] = useState(null);
    const [hasFetched, setHasFetched] = useState(false);
    const [latest990, setLatest990] = useState(null);
    const [selectedOrganizationName, setSelectedOrganizationName] = useState(null);
    const [description, setDescription] = useState('');
    const [awardsReceived, setAwardsReceived] = useState([]);
    const [awardsGranted, setAwardsGranted] = useState([]);

    // const [showMagicFirstDraftModal, setShowMagicFirstDraftModal] = useState(false);

    const handleMagicDraftButtonClicked = () => {
        if (membershipDetails) {
            setShowMagicFirstDraftModal(true);
        } else {
            setShowPaywallModal(true);            
        }
    }


    // Fetch organization data
    useEffect(() => {
        const fetchOrganizationData = async () => {
            if (ein) {
                setShowLoadingModal(true);
                try {
                    const data = await fetchLatest990FromSupabase(ein);
                    if (data) {
                        setLatest990(data);
                        setOrganization(data);
                    } else {
                        // Set organization to null to trigger the "not found" message
                        setOrganization(null);
                    }
                } catch (error) {
                    console.error("Error fetching organization data:", error);
                    setOrganization(null);
                } finally {
                    setShowLoadingModal(false);
                    setHasFetched(true);
                }
            }
        };
        fetchOrganizationData();
    }, [ein, setShowLoadingModal]);

    // Fetch awards data when latest990 is available
    useEffect(() => {
        if (latest990) {
            Promise.all([
                fetchAwardsReceivedFromSupabase(latest990.ein),
                fetchAwardsGrantedFromSupabase(latest990.ein)
            ]).then(([awardsReceived, awardsGranted]) => {
                setAwardsReceived(awardsReceived);
                setAwardsGranted(awardsGranted);
            }).catch(error => {
                console.error("Error loading data:", error);
            });
        }
    }, [latest990]);

    // Set organization name
    useEffect(() => {
        if (latest990?.business_name) {
            setSelectedOrganizationName(latest990.business_name);
            setFunderNameForMagicDraft(latest990.business_name);
        } else if (awardsReceived.length > 0) {
            const recipientName = awardsReceived[0].recipient_name;
            setSelectedOrganizationName(recipientName);
        }
    }, [latest990, awardsReceived]);

    // Set description
    useEffect(() => {
        if (latest990) {
            const descriptions = [
                latest990.activity_or_mission_desc,
                latest990.desc,
                latest990.mission_desc
            ].filter(Boolean);

            const longestDescription = descriptions.reduce((longest, current) =>
                current.length > longest.length ? current : longest, "");

            setDescription(longestDescription);
            setFunderDescriptionForMagicDraft(longestDescription);
        }
    }, [latest990]);

    // Column definitions for the grid - Mobile responsive
    const awardsReceivedColumnDefs = [
        { 
            headerName: "Tax Year", 
            field: "tax_year",
            width: 80,
            minWidth: 80,
            maxWidth: 100
        },
        { 
            headerName: "Funder Name", 
            field: "funder_name",
            cellRenderer: FunderRenderer,
            flex: 1,
            minWidth: 120
        },
        { 
            headerName: "Recipient Name", 
            field: "recipient_name",
            filter: "agTextColumnFilter",
            cellRenderer: RecipientRenderer,
            flex: 1,
            minWidth: 120
        },
        { 
            headerName: "Amount", 
            field: "cash_grant_amt", 
            valueGetter: ({ data }) => data.cash_grant_amt ? parseFloat(data.cash_grant_amt) : 0,
            valueFormatter: ({ value }) => `$${value.toLocaleString()}`,
            filter: "agNumberColumnFilter",
            width: 100,
            minWidth: 80,
            maxWidth: 120
        },
        { 
            headerName: "Purpose", 
            field: "purpose",
            filter: "agTextColumnFilter",
            flex: 1,
            minWidth: 100
        },
        { 
            headerName: "Recipient NTEE", 
            field: "ntee_codes.ntee_full",
            width: 100,
            minWidth: 80,
            maxWidth: 120
        },
        { 
            headerName: "Recipient City", 
            field: "city_name",
            filter: "agTextColumnFilter",
            width: 100,
            minWidth: 80,
            maxWidth: 120
        },
        { 
            headerName: "Recipient State", 
            field: "state_abbreviation_cd",
            filter: "agTextColumnFilter",
            width: 80,
            minWidth: 60,
            maxWidth: 80
        },
        { 
            headerName: "Recipient ZIP", 
            field: "zip_code",
            filter: "agTextColumnFilter",
            width: 80,
            minWidth: 60,
            maxWidth: 80
        }
    ];

    // Column definitions for awards granted - Mobile responsive
    const awardsGrantedColumnDefs = [
        { 
            headerName: "Tax Year", 
            field: "tax_year",
            width: 80,
            minWidth: 80,
            maxWidth: 100
        },
        { 
            headerName: "Funder Name", 
            field: "funder_name",
            cellRenderer: FunderRenderer,
            flex: 1,
            minWidth: 120
        },
        { 
            headerName: "Recipient Name", 
            field: "recipient_name",
            filter: "agTextColumnFilter",
            cellRenderer: RecipientRenderer,
            flex: 1,
            minWidth: 120
        },
        { 
            headerName: "Amount", 
            field: "cash_grant_amt", 
            valueGetter: ({ data }) => data.cash_grant_amt ? parseFloat(data.cash_grant_amt) : 0,
            valueFormatter: ({ value }) => `$${value.toLocaleString()}`,
            filter: "agNumberColumnFilter",
            width: 100,
            minWidth: 80,
            maxWidth: 120
        },
        { 
            headerName: "Purpose", 
            field: "purpose",
            filter: "agTextColumnFilter",
            flex: 1,
            minWidth: 100
        },
        { 
            headerName: "Recipient NTEE", 
            field: "ntee_codes.ntee_full",
            width: 100,
            minWidth: 80,
            maxWidth: 120
        },
        { 
            headerName: "Recipient City", 
            field: "city_name",
            filter: "agTextColumnFilter",
            width: 100,
            minWidth: 80,
            maxWidth: 120
        },
        { 
            headerName: "Recipient State", 
            field: "state_abbreviation_cd",
            filter: "agTextColumnFilter",
            width: 80,
            minWidth: 60,
            maxWidth: 80
        },
        { 
            headerName: "Recipient ZIP", 
            field: "zip_code",
            filter: "agTextColumnFilter",
            width: 80,
            minWidth: 60,
            maxWidth: 80
        }
    ];

    if (!ein) {
        return <div className='flex justify-center text-gray-500 items-center min-h-screen w-screen'>No organization EIN provided</div>;
    }

    if (organization === null && hasFetched) {
        return <div className='flex justify-center text-gray-500 items-center min-h-screen w-screen'>
            <div className='text-center p-4'>
                <p className='text-lg sm:text-xl mb-2'>Oops! We don't have data for this organization.</p>
                <p className='text-sm mb-6'>The organization with EIN {ein} was not found in our database.</p>
                <button 
                    onClick={() => window.location.href = '/'}
                    className='bg-indigo-500 hover:bg-indigo-700 text-white font-bold py-2 px-4 rounded'
                >
                    Return Home
                </button>
            </div>
        </div>;
    }

    return (
        <div className="p-4 sm:p-6 lg:p-8 text-left pt-8 sm:pt-12">
            {/* Header - Mobile responsive */}
            <div className='flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 sm:gap-0'>
                <p className='text-2xl sm:text-3xl lg:text-4xl font-bold break-words'>
                    {selectedOrganizationName ? 
                        selectedOrganizationName.split(' ').map(word => 
                            word.charAt(0).toUpperCase() + word.slice(1).toLowerCase()
                        ).join(' ') 
                        : 'Loading...'
                    }
                </p>
                <div className='flex space-x-2 w-full sm:w-auto'>
                    <button 
                        className='bg-indigo-500 hover:bg-indigo-700 text-white font-bold py-2 px-3 sm:px-4 rounded text-sm sm:text-base w-full sm:w-auto' 
                        onClick={handleMagicDraftButtonClicked}
                    >
                        Generate Magic Draft
                    </button>
                </div>
            </div>
            
            {/* Status badges - Mobile responsive */}
            <div className="mt-3 flex flex-wrap justify-start items-center gap-2 sm:gap-4">
                {awardsReceived.length > 0 && <div className="bg-green-200 text-green-900 p-2 rounded-lg text-xs sm:text-sm">Grant Winner</div>}
                {awardsGranted.length > 0 && <div className="bg-blue-200 text-blue-800 p-2 rounded-lg text-xs sm:text-sm">Grant Maker</div>}
            </div>

            {/* General Details - Mobile responsive table */}
            <h3 className="orgDetailsLabel text-lg sm:text-xl">General Details</h3>
            <div className="bg-gray-100 p-3 sm:p-4 rounded-md mb-4">
                <div className="space-y-3 sm:space-y-0">
                    {latest990?.website_address_txt && (
                        <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center border-b border-gray-300 pb-2 sm:pb-0">
                            <span className="font-medium text-sm sm:text-base">Website</span>
                            <a href={`http://${latest990.website_address_txt}`} target="_blank" rel="noopener noreferrer" className="underline text-sm sm:text-base break-all sm:break-normal">{latest990.website_address_txt}</a>
                        </div>
                    )}
                    {latest990?.address_line_1_text && latest990?.city_name && latest990?.state_abbreviation_cd && latest990?.zip_code && (
                        <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center border-b border-gray-300 pb-2 sm:pb-0">
                            <span className="font-medium text-sm sm:text-base">Address</span>
                            <span className="text-sm sm:text-base text-right">{`${latest990.address_line_1_text}, ${latest990.city_name}, ${latest990.state_abbreviation_cd} ${latest990.zip_code}`}</span>
                        </div>
                    )}
                    {latest990?.phone_number && (
                        <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center border-b border-gray-300 pb-2 sm:pb-0">
                            <span className="font-medium text-sm sm:text-base">Phone Number</span>
                            <span className="text-sm sm:text-base text-right">{latest990?.phone_number?.replace(/(\d{3})(\d{3})(\d{4})/, '($1) $2-$3')}</span>
                        </div>
                    )}                  
                    <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center">
                        <span className="font-medium text-sm sm:text-base">EIN</span>
                        <span className="text-sm sm:text-base text-right">{latest990?.ein}</span>
                    </div>
                </div>
            </div>
            
            {/* Mission/Description - Mobile responsive */}
            {description && (
                <>
                    <h3 className="orgDetailsLabel text-lg sm:text-xl">Mission / Description</h3>
                    <div className="bg-gray-100 p-3 sm:p-4 rounded-md mb-4">
                        <div className="flex flex-col sm:flex-row sm:justify-between sm:items-start">
                            <span className="font-medium text-sm sm:text-base mb-2 sm:mb-0">Description</span>
                            <span className="text-sm sm:text-base text-right sm:text-left">{description}</span>
                        </div>
                    </div>
                </>
            )}

            {/* Financial Details - Mobile responsive */}
            {(
                latest990?.total_assets_boy_amt != null ||
                latest990?.total_assets_eoy_amt != null ||
                latest990?.cy_total_revenue_amt != null ||
                latest990?.cy_grants_and_similar_paid_amt != null
            ) && (
                <>
                    <h3 className="orgDetailsLabel text-lg sm:text-xl">Financial Details</h3>
                    <div className="bg-gray-100 p-3 sm:p-4 rounded-md mb-4">
                        <div className="space-y-3 sm:space-y-0">
                            {latest990?.total_assets_boy_amt != null && (
                                <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center border-b border-gray-300 pb-2 sm:pb-0">
                                    <span className="font-medium text-sm sm:text-base">Total Assets (Beginning of Year)</span>
                                    <span className="text-sm sm:text-base text-right">${latest990.total_assets_boy_amt.toLocaleString()}</span>
                                </div>
                            )}
                            {latest990?.total_assets_eoy_amt != null && (
                                <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center border-b border-gray-300 pb-2 sm:pb-0">
                                    <span className="font-medium text-sm sm:text-base">Total Assets (End of Year)</span>
                                    <span className="text-sm sm:text-base text-right">${latest990.total_assets_eoy_amt.toLocaleString()}</span>
                                </div>
                            )}
                            {latest990?.total_assets_boy_amt != null && latest990?.total_assets_eoy_amt != null && (
                                <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center border-b border-gray-300 pb-2 sm:pb-0">
                                    <span className="font-medium text-sm sm:text-base">Growth Rate in Assets (YoY)</span>
                                    <span className="text-sm sm:text-base text-right">
                                        {`${((latest990.total_assets_eoy_amt - latest990.total_assets_boy_amt) / latest990.total_assets_boy_amt * 100).toFixed(2)}%`}
                                    </span>
                                </div>
                            )}
                            {latest990?.cy_total_revenue_amt != null && (
                                <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center border-b border-gray-300 pb-2 sm:pb-0">
                                    <span className="font-medium text-sm sm:text-base">Latest Revenue</span>
                                    <span className="text-sm sm:text-base text-right">${latest990.cy_total_revenue_amt.toLocaleString()}</span>
                                </div>
                            )}
                            {latest990?.cy_grants_and_similar_paid_amt != null && (
                                <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center">
                                    <span className="font-medium text-sm sm:text-base">Latest Expenses</span>
                                    <span className="text-sm sm:text-base text-right">${latest990.cy_grants_and_similar_paid_amt.toLocaleString()}</span>
                                </div>
                            )}
                        </div>
                    </div>
                </>
            )}

            {/* Giving History - Mobile responsive */}
            {awardsGranted.length > 0 && (
                <>
                    <h3 className="orgDetailsLabel text-lg sm:text-xl">Giving History</h3>
                    <div className="bg-gray-100 p-3 sm:p-4 rounded-md mb-4">
                        <div className="space-y-3 sm:space-y-0">
                            <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center border-b border-gray-300 pb-2 sm:pb-0">
                                <span className="font-medium text-sm sm:text-base">Grants Given ($)</span>                                    
                                <span className="text-sm sm:text-base text-right">
                                    ${awardsGranted.reduce((sum, award) => sum + parseFloat(award.cash_grant_amt || 0), 0).toLocaleString()}
                                </span>
                            </div>
                            <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center border-b border-gray-300 pb-2 sm:pb-0">
                                <span className="font-medium text-sm sm:text-base">Grants Given (#)</span>
                                <span className="text-sm sm:text-base text-right">
                                    {awardsGranted.length.toLocaleString()}
                                </span>
                            </div>
                            <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center">
                                <span className="font-medium text-sm sm:text-base">Average Grant Size</span>
                                <span className="text-sm sm:text-base text-right">
                                    ${(Math.round(awardsGranted.reduce((sum, award) => sum + parseFloat(award.cash_grant_amt || 0), 0) / awardsGranted.length)).toLocaleString()}
                                </span>
                            </div>
                        </div>
                    </div>
                </>
            )}

            {/* Awards Granted - Mobile responsive grid */}
            <h3 className="orgDetailsLabel text-lg sm:text-xl mb-4">Awards Granted ({awardsGranted.length === 1000 ? '1,000+' : awardsGranted.length.toLocaleString()})</h3>
            {awardsGranted.length > 0 ? (
                <div className="ag-theme-alpine mb-8" style={{ height: '300px', width: '100%', fontSize: '12px' }}>
                    <AgGridReact
                        rowData={awardsGranted}
                        columnDefs={awardsGrantedColumnDefs}
                        defaultColDef={{
                            resizable: true,
                            sortable: true,
                            filter: true,
                            minWidth: 60,
                            flex: 1,
                            cellStyle: { fontSize: '0.625rem' },
                            headerClass: 'text-xs'
                        }}
                        domLayout="normal"
                        suppressColumnVirtualisation={false}
                        suppressRowVirtualisation={false}
                    />
                </div>
            ) : (
                <p className="text-sm sm:text-base mb-8">No awards granted available.</p>
            )}

            {/* Awards Received - Mobile responsive grid */}
            <h3 className="orgDetailsLabel text-lg sm:text-xl mb-4">Awards Received ({awardsReceived.length})</h3>
            {awardsReceived.length > 0 ? (
                <div className="ag-theme-alpine" style={{ height: '300px', width: '100%', fontSize: '12px' }}>
                    <AgGridReact
                        rowData={awardsReceived}
                        columnDefs={awardsReceivedColumnDefs}
                        defaultColDef={{
                            resizable: true,
                            sortable: true,
                            filter: true,
                            minWidth: 60,
                            flex: 1,
                            cellStyle: { fontSize: '0.625rem' },
                            headerClass: 'text-xs'
                        }}
                        domLayout="normal"
                        suppressColumnVirtualisation={false}
                        suppressRowVirtualisation={false}
                    />
                </div>
            ) : (
                <p className="text-sm sm:text-base">No awards received available.</p>
            )}
        {/* {showMagicFirstDraftModal && (
            <MagicFirstDraftModal 
                setShowMagicFirstDraftModal={setShowMagicFirstDraftModal}
                currentProgram={currentProgram}
                setShowLoadingModal={setShowLoadingModal}
                prepopulatedFunderName={selectedOrganizationName}
                prepopulatedFunderDescription={description}
            />
        )} */}
        </div>
    );
}

export default OrganizationProfilePage;